import { useCallback, useEffect, useRef } from "react";

/**
 * Browser-native narration via the Web Speech API (no API key, works offline).
 * Picks a warm English voice, speaks slowly with a gentle pitch.
 * Gracefully no-ops where speech synthesis is unavailable.
 */
export function useNarration() {
  const voiceRef = useRef(null);
  const supported = typeof window !== "undefined" && "speechSynthesis" in window;

  useEffect(() => {
    if (!supported) return;
    const pick = () => {
      const voices = window.speechSynthesis.getVoices() || [];
      const pref =
        voices.find((v) => /en-GB/i.test(v.lang) && /female|libby|sonia|hazel/i.test(v.name)) ||
        voices.find((v) => /samantha|karen|moira|tessa|fiona|google uk english female/i.test(v.name)) ||
        voices.find((v) => /en-GB/i.test(v.lang)) ||
        voices.find((v) => /^en/i.test(v.lang)) ||
        voices[0];
      voiceRef.current = pref || null;
    };
    pick();
    window.speechSynthesis.addEventListener("voiceschanged", pick);
    return () => window.speechSynthesis.removeEventListener("voiceschanged", pick);
  }, [supported]);

  const speak = useCallback(
    (textOrSegments, opts = {}) => {
      if (!supported || !textOrSegments) return;
      const segments = (Array.isArray(textOrSegments) ? textOrSegments : [textOrSegments]).filter(Boolean);
      try {
        window.speechSynthesis.cancel();
        // Queue each short segment separately — the browser inserts a natural
        // pause between utterances and short clips avoid the long-speech cut-off.
        segments.forEach((seg) => {
          const u = new SpeechSynthesisUtterance(String(seg));
          if (voiceRef.current) u.voice = voiceRef.current;
          u.rate = opts.rate ?? 0.9;
          u.pitch = opts.pitch ?? 1.2;
          u.volume = opts.volume ?? 1;
          window.speechSynthesis.speak(u);
        });
      } catch (e) {
        /* no-op */
      }
    },
    [supported],
  );

  const cancel = useCallback(() => {
    if (!supported) return;
    try {
      window.speechSynthesis.cancel();
    } catch (e) {
      /* no-op */
    }
  }, [supported]);

  return { speak, cancel, supported };
}
